/**
 * EtToolbox — het rijke antwoordveld voor berekenvragen (Facet-blauwdruk §3).
 *
 * Leerlingen kennen dit van hun digitale examen: een antwoordveld met knoppen
 * om een breuk, macht, wortel of tabel in te voegen, plus de berekening die
 * de rekenmachine erin plakt. Eigen implementatie in TestWisely-stijl.
 *
 * - contentEditable met HTML-snippets (breuk = teller boven noemer, macht =
 *   sup, wortel met streep, tabel met rij/kolom-knoppen zodra je erin staat)
 * - serialisatie naar platte tekst via toolboxNaarTekst (utils/etToolbox.js),
 *   zodat opslag/nakijken gewoon "3/4", "5^2", "√(16)" en |‑tabellen ziet
 * - voegRegelToe(regel) voor de rekenmachine-plak (imperative handle)
 */
import React, { useRef, useEffect, useImperativeHandle, forwardRef, useState } from 'react';
import { toolboxNaarTekst } from '../../utils/etToolbox';
import './EtToolbox.css';

const EtToolbox = forwardRef(function EtToolbox({ html, onWijzig, disabled, ariaLabel }, ref) {
  const veldRef = useRef(null);
  const [inTabel, setInTabel] = useState(false);

  // Externe html alleen zetten als die echt afwijkt (anders springt de cursor).
  useEffect(() => {
    const veld = veldRef.current;
    if (veld && (html ?? '') !== veld.innerHTML) veld.innerHTML = html ?? '';
  }, [html]);

  // Review-fix #7: de breuk-/wortel-eilandjes hebben een eigen
  // contenteditable-attribuut en blijven anders bewerkbaar ná Nakijken.
  useEffect(() => {
    const veld = veldRef.current;
    if (!veld) return;
    veld.querySelectorAll('.etx-teller, .etx-noemer, .etx-wortel-inhoud')
      .forEach((el) => el.setAttribute('contenteditable', disabled ? 'false' : 'true'));
  }, [disabled, html]);

  const meld = () => {
    if (disabled) return;   // review-fix #7: geen mutaties meer na Nakijken
    const veld = veldRef.current;
    if (veld && onWijzig) onWijzig(veld.innerHTML, toolboxNaarTekst(veld));
  };

  // ── invoegen op de cursorpositie ──
  const voegHtmlIn = (snippet, focusSelector) => {
    const veld = veldRef.current;
    if (!veld || disabled) return;
    veld.focus();
    const sel = window.getSelection();
    let range = sel && sel.rangeCount ? sel.getRangeAt(0) : null;
    if (!range || !veld.contains(range.commonAncestorContainer)) {
      range = document.createRange();
      range.selectNodeContents(veld);
      range.collapse(false);
    }
    const houder = document.createElement('span');
    houder.innerHTML = snippet;
    const nieuwe = [...houder.childNodes];
    // Eén fragment-insert behoudt de kindervolgorde; herhaald insertNode op een
    // collapsed range zette de nodes omgekeerd (review-fix #6). deleteContents
    // zorgt dat een actieve selectie netjes vervangen wordt.
    const fragment = document.createDocumentFragment();
    nieuwe.forEach((n) => fragment.appendChild(n));
    range.deleteContents();
    range.insertNode(fragment);
    // cursor in het eerste invulbare deel zetten
    const doel = focusSelector && nieuwe
      .map((n) => (n.nodeType === 1 ? (n.matches?.(focusSelector) ? n : n.querySelector?.(focusSelector)) : null))
      .find(Boolean);
    if (doel) {
      const r2 = document.createRange();
      r2.selectNodeContents(doel);
      r2.collapse(true);
      sel.removeAllRanges(); sel.addRange(r2);
    } else {
      range.collapse(false);
      sel.removeAllRanges(); sel.addRange(range);
    }
    meld();
    bijSelectie();   // review-fix #10: knopstaat klopt direct na invoegen
  };

  const voegBreukIn = () => voegHtmlIn(
    '<span class="etx-breuk" contenteditable="false">' +
    '<span class="etx-teller" contenteditable="true"> </span>' +
    '<span class="etx-noemer" contenteditable="true"> </span></span> ',
    '.etx-teller');
  const voegMachtIn = () => voegHtmlIn('<sup> </sup> ', 'sup');
  const voegWortelIn = () => voegHtmlIn(
    '<span class="etx-wortel" contenteditable="false">√' +
    '<span class="etx-wortel-inhoud" contenteditable="true"> </span></span> ',
    '.etx-wortel-inhoud');
  const voegTabelIn = () => voegHtmlIn(
    '<table class="etx-tabel"><tbody>' +
    '<tr><td> </td><td> </td><td> </td></tr>' +
    '<tr><td> </td><td> </td><td> </td></tr>' +
    '</tbody></table>', 'td');

  // ── tabelbewerking: werkt op de tabel waar de cursor in staat ──
  const actieveTabel = () => {
    const sel = window.getSelection();
    if (!sel || !sel.rangeCount) return null;
    let n = sel.getRangeAt(0).commonAncestorContainer;
    while (n && n !== veldRef.current) {
      if (n.nodeType === 1 && n.tagName === 'TABLE') return n;
      n = n.parentNode;
    }
    return null;
  };
  const bijSelectie = () => setInTabel(!!actieveTabel());
  const rijErbij = () => {
    const t = actieveTabel(); if (!t) return;
    const rij = t.querySelector('tr');
    const nieuw = document.createElement('tr');
    for (let k = 0; k < rij.children.length; k++) { const td = document.createElement('td'); td.innerHTML = ' '; nieuw.appendChild(td); }
    t.querySelector('tbody').appendChild(nieuw); meld();
  };
  const kolomErbij = () => {
    const t = actieveTabel(); if (!t) return;
    t.querySelectorAll('tr').forEach((tr) => { const td = document.createElement('td'); td.innerHTML = ' '; tr.appendChild(td); });
    meld();
  };
  const rijEraf = () => {
    const t = actieveTabel(); if (!t) return;
    const rijen = t.querySelectorAll('tr');
    if (rijen.length > 1) { rijen[rijen.length - 1].remove(); meld(); }
  };
  const kolomEraf = () => {
    const t = actieveTabel(); if (!t) return;
    t.querySelectorAll('tr').forEach((tr) => { if (tr.children.length > 1) tr.lastElementChild.remove(); });
    meld();
  };

  useImperativeHandle(ref, () => ({
    // Letterplankje: één teken op de cursorpositie invoegen.
    voegTekenIn(teken) {
      const veld = veldRef.current;
      if (!veld || disabled) return;
      veld.focus();
      const sel = window.getSelection();
      let range = sel && sel.rangeCount ? sel.getRangeAt(0) : null;
      if (!range || !veld.contains(range.commonAncestorContainer)) {
        range = document.createRange();
        range.selectNodeContents(veld); range.collapse(false);
      }
      range.deleteContents();
      const node = document.createTextNode(teken);
      range.insertNode(node);
      range.setStartAfter(node); range.collapse(true);
      sel.removeAllRanges(); sel.addRange(range);
      meld();
    },
    voegRegelToe(regel) {
      const veld = veldRef.current;
      if (!veld) return;
      const div = document.createElement('div');
      div.textContent = regel;
      veld.appendChild(div);
      meld();
    },
  }));

  return (
    <div className="etx-wrap">
      {!disabled && (
        <div className="etx-balk" role="toolbar" aria-label="Toolbox">
          <button type="button" onClick={voegBreukIn} aria-label="Breuk invoegen">
            <span className="etx-balk-breuk"><b>x</b><i>y</i></span>
          </button>
          <button type="button" onClick={voegMachtIn} aria-label="Macht invoegen">x<sup>y</sup></button>
          <button type="button" onClick={voegWortelIn} aria-label="Wortel invoegen">√</button>
          <button type="button" onClick={voegTabelIn} aria-label="Tabel invoegen">⊞ tabel</button>
          {inTabel && (
            <span className="etx-tabel-knoppen">
              <button type="button" onClick={rijErbij} aria-label="Rij toevoegen">rij +</button>
              <button type="button" onClick={kolomErbij} aria-label="Kolom toevoegen">kolom +</button>
              <button type="button" onClick={rijEraf} aria-label="Rij verwijderen">rij −</button>
              <button type="button" onClick={kolomEraf} aria-label="Kolom verwijderen">kolom −</button>
            </span>
          )}
        </div>
      )}
      <div ref={veldRef}
        className={`etx-veld ${disabled ? 'uit' : ''}`}
        contentEditable={!disabled}
        suppressContentEditableWarning
        role="textbox" aria-multiline="true" aria-label={ariaLabel || 'Antwoordveld'}
        data-placeholder="Laat hier zien hoe je aan je antwoord komt…"
        onInput={meld}
        onKeyUp={bijSelectie} onMouseUp={bijSelectie} onFocus={bijSelectie}
        onKeyDown={(e) => {
          // Review-fix #8: Enter in de teller springt naar de noemer; Enter in
          // noemer/wortel springt het eilandje uit — geen regelbreuk ín de breuk.
          if (e.key !== 'Enter') return;
          const sel = window.getSelection();
          const anker = sel && sel.anchorNode;
          const el = anker && (anker.nodeType === 1 ? anker : anker.parentElement);
          if (!el) return;
          const teller = el.closest('.etx-teller');
          const eiland = el.closest('.etx-teller, .etx-noemer, .etx-wortel-inhoud');
          if (!eiland) return;   // Enter in het gewone veld mag een nieuwe regel maken
          e.preventDefault();
          const r = document.createRange();
          if (teller) {
            const noemer = teller.parentElement.querySelector('.etx-noemer');
            if (noemer) { r.selectNodeContents(noemer); r.collapse(false); }
          } else {
            const wrapper = eiland.closest('.etx-breuk, .etx-wortel');
            if (wrapper) { r.setStartAfter(wrapper); r.collapse(true); }
          }
          sel.removeAllRanges(); sel.addRange(r);
        }}
      />
    </div>
  );
});

export default EtToolbox;
